import { Outlet } from "react-router";
import type { CSSProperties, ReactNode } from "react";
import { NewVersionNotice } from "@/components/shared/NewVersionNotice";
import { ToastSurface } from "@/components/ui/ToastSurface";
import { buttonChrome } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SHELL_HEADER_HEIGHT, V2Header } from "../layout/Header";
import type { Crumb } from "../layout/Header";
import { MaybeLink } from "../layout/MaybeLink";
import { GoesBack } from "../layout/wayfinding";
import { CLIENTS_PATH } from "./paths";

/**
 * The pages above a draft: the attorney's clients and a client's drafts.
 *
 * The shell's header and nothing of its rail, since no draft is open: a column
 * of its own, as wide as a list of clients reads, that scrolls under a fixed
 * header.
 */
export function ClientsLayout() {
  return (
    <div
      data-testid="clients-root"
      className="flex h-screen flex-col overflow-hidden bg-background text-foreground"
      style={
        {
          "--shell-header-h": `${String(SHELL_HEADER_HEIGHT)}px`,
        } as CSSProperties
      }
    >
      <Outlet />
      {/* Under the page, in a row of its own: the scrolling column above is shorter by it. */}
      <NewVersionNotice />
      <ToastSurface />
    </div>
  );
}

/** The way from a page back up to the one it sits under. */
export function BackLink({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) {
  return (
    <MaybeLink
      to={to}
      className={cn(
        buttonChrome({ variant: "quiet" }),
        "-ml-1 mb-3.5 gap-1.5 pr-2 pl-1 text-[12.5px] hover:bg-muted hover:text-primary",
      )}
    >
      <GoesBack />
      {children}
    </MaybeLink>
  );
}

export function BackToClients() {
  return <BackLink to={CLIENTS_PATH}>All clients</BackLink>;
}

/** One client page: the header with its trail, and the page under it. */
export function ClientsPageFrame({
  crumbs,
  children,
}: {
  crumbs: readonly Crumb[];
  children: ReactNode;
}) {
  return (
    <>
      <V2Header crumbs={crumbs} />
      <main className="min-h-0 flex-1 overflow-y-auto px-8">
        <div className="mx-auto max-w-[920px] pb-24">{children}</div>
      </main>
    </>
  );
}
